import React, { useState, useEffect, useMemo } from 'react'
import { useFormik } from 'formik';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import * as yup from 'yup';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Breadcrums from '../../../components/company_panel/common/breadcrumbs';
import { capitalizeFirstLetter, getBrandId, getCompany } from '../../../helpers/commonFunctions';
import { addMenuItems, EditMenuItems, GetMenuItems, getAllMenuCategoryItems } from '../../../services/company_panel/categoryService'
import Swal from 'sweetalert2'
import { updateMenuCategoryOrder } from '../../../services/company_panel/menuCategoryService';
import Loader from "../../../components/company_panel/common/loader";

// A category, sub category or menu item that is switched off (status false). It
// can still be picked for the menu card, but customers will not see it until it
// is turned back on, so it is shown in red rather than hidden.
const isInactive = (category) => Boolean(category) && (category.status === false || category.status === 0 || category.status === '0');

const INACTIVE_TITLE = 'Switched off: it will not show on the menu until it is turned back on.';

// On a collapsed row, so what is switched off inside it is visible without
// opening it: sub categories on a category row, items on a sub category row.
const inactiveCountHint = (list, singular, plural) => {
    const count = (list || []).filter(isInactive).length;
    if (!count) return null;
    return <span className="menu-inactive-hint" title={INACTIVE_TITLE}>{count} inactive {count === 1 ? singular : plural}</span>;
};
const inactiveSubCategoriesHint = (category) => inactiveCountHint(category?.SubCategories, 'sub category', 'sub categories');
const inactiveItemsHint = (subCategory) => inactiveCountHint(subCategory?.menu_items, 'item', 'items');

const EditMenu = () => {

    const [categories, setCategories] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState({});
    const [selectedSubCategories, setSelectedSubCategories] = useState({});
    const [initialSelectedCategories, setInitialSelectedCategories] = useState({});
    const [initialSelectedSubCategories, setInitialSelectedSubCategories] = useState({});
    const [initialOrders, setInitialOrders] = useState({ categories: {}, subCategories: {} });
    const [selectedMenuItems, setSelectedMenuItems] = useState({});
    const navigate = useNavigate();
    const params = useParams();
    const [searchParams] = useSearchParams();
    const [data, setData] = useState({});
    const [isBrandChanged, setIsBrandChanged] = useState(false);
    const [currentBrand] = useState(getBrandId());
    const [loader, setLoader] = useState(false);
    const [menuSearch, setMenuSearch] = useState('');
    // Copy re-uses this screen: same detail call and prefill, but saves a new menu.
    const isCopy = searchParams.get('copy') === 'true';

    const breadcrumbs = [
        {
            title: "Menu ",
            url: `/company/menu/menu/menu_card/list/${searchParams.get('pageno') || 1}`
        },
        {
            title: isCopy ? "Copy Menu" : "Edit Menu",
            url: ""
        },
    ]

    useMemo(() => {
        document.addEventListener('brand-changed', function () {
            setIsBrandChanged(!isBrandChanged);
        });
        if (currentBrand != getBrandId()) {
            navigate(`/company/menu/menu/menu_card/list/${searchParams.get('pageno') || 1}`);
        }
    });

    useEffect(() => {
        getAllMenuCategoryItems({ brand_id: getBrandId(), menu_id: params.id }).then(data => {
            if (data && data.data && data.data.response) {
                const menuItems = data.data.response;
                let categories = [];
                for (let i = 0; i < menuItems.length; i++) {
                    let subCategories = [];
                    for (let j = 0; j < menuItems[i].SubCategories.length; j++) {
                        if (menuItems[i].SubCategories[j].menu_items.length) {
                            subCategories.push(menuItems[i].SubCategories[j]);
                        }
                    }
                    if (subCategories.length) {
                        categories.push({ ...menuItems[i], SubCategories: subCategories })
                    }
                }
                setCategories(categories);

                // Snapshot the loaded fetching_order so submit can send only the rows that changed.
                const categoryOrders = {};
                const subCategoryOrders = {};
                categories.forEach((category) => {
                    categoryOrders[category.id] = category.fetching_order;
                    (category.SubCategories || []).forEach((sub) => {
                        subCategoryOrders[sub.id] = sub.fetching_order;
                    });
                });
                setInitialOrders({ categories: categoryOrders, subCategories: subCategoryOrders });
            }
        }).catch(error => {
            console.log("error=====>", error);
        });
    }, [isBrandChanged]);

    useEffect(() => {
        GetMenuItems({ id: params.id }).then(response => {
            if (response && response.data && response.data.response) {
                setData(response.data.response);

                const selectedCategoriesData = {};
                const selectedSubCategoriesData = {};
                const selectedMenuItemsData = {};

                response.data.response['menu-item-relations'].forEach(itemRelation => {
                    const menuItem = itemRelation.menu_item;
                    const subCategoryId = menuItem.sub_category_id;
                    const categoryId = menuItem.menu_category.parent_id;

                    selectedCategoriesData[categoryId] = true;
                    selectedSubCategoriesData[subCategoryId] = true;
                    selectedMenuItemsData[menuItem.id] = true;
                });
                setSelectedCategories(selectedCategoriesData);
                setSelectedSubCategories(selectedSubCategoriesData);
                setSelectedMenuItems(selectedMenuItemsData);
                setInitialSelectedCategories({ ...selectedCategoriesData });
                setInitialSelectedSubCategories({ ...selectedSubCategoriesData });
            }
        }).catch(error => {
            console.error("error=====>", error);
        });
    }, [params.id]);

    const handleCategoryChange = (categoryId) => {
        setSelectedCategories(prev => {
            const newSelection = { ...prev, [categoryId]: !prev[categoryId] };
            const updatedSubCategories = { ...selectedSubCategories };
            const updatedMenuItems = { ...selectedMenuItems };

            if (!newSelection[categoryId]) {
                const category = categories.find(cat => cat.id === categoryId);
                category.SubCategories.forEach(sub => {
                    delete updatedSubCategories[sub.id];
                    sub.menu_items.forEach(menuItem => {
                        delete updatedMenuItems[menuItem.id];
                    });
                });
            } else {
                const category = categories.find(cat => cat.id === categoryId);
                category.SubCategories.forEach(sub => {
                    updatedSubCategories[sub.id] = false;
                    sub.menu_items.forEach(menuItem => {
                        updatedMenuItems[menuItem.id] = false;
                    });
                });
            }

            setSelectedSubCategories(updatedSubCategories);
            setSelectedMenuItems(updatedMenuItems);

            return newSelection;
        });
    };

    const handleSubCategoryChange = (subCategoryId) => {
        const newSelection = { ...selectedSubCategories, [subCategoryId]: !selectedSubCategories[subCategoryId] };
        const updatedMenuItems = { ...selectedMenuItems };

        const parentCategory = categories.find(cat => cat.SubCategories.some(sub => sub.id === subCategoryId));
        const subCategory = parentCategory.SubCategories.find(sub => sub.id === subCategoryId);

        if (!newSelection[subCategoryId]) {
            subCategory.menu_items.forEach(menuItem => {
                delete updatedMenuItems[menuItem.id];
            });
        } else {
            subCategory.menu_items.forEach(menuItem => {
                updatedMenuItems[menuItem.id] = false;
            });
        }

        setSelectedMenuItems(updatedMenuItems);
        setSelectedSubCategories(newSelection);

        // Cascade up: if all subcategories of parent category are unchecked, uncheck category
        if (!newSelection[subCategoryId]) {
            const allSubsUnchecked = parentCategory.SubCategories.every(sub => !newSelection[sub.id]);
            if (allSubsUnchecked) {
                setSelectedCategories(prev => ({ ...prev, [parentCategory.id]: false }));
            }
        }
    };

    // const handleMenuItemChange = (menuItemId) => {
    //     setSelectedMenuItems(prev => ({
    //         ...prev,
    //         [menuItemId]: !prev[menuItemId]
    //     }));
    // };

    const handleMenuItemChange = (menuItemId) => {
        const updatedMenuItems = {
            ...selectedMenuItems,
            [menuItemId]: !selectedMenuItems[menuItemId]
        };
        setSelectedMenuItems(updatedMenuItems);

        // Update Formik's value for menu_items_list
        const selectedItems = Object.keys(updatedMenuItems).filter(id => updatedMenuItems[id]);
        formik.setFieldValue('menu_items_list', selectedItems);

        // Find parent subcategory + category
        let parentSubCategory = null;
        let parentCategory = null;
        for (const cat of categories) {
            for (const sub of cat.SubCategories) {
                if (sub.menu_items.some(mi => mi.id === menuItemId)) {
                    parentSubCategory = sub;
                    parentCategory = cat;
                    break;
                }
            }
            if (parentSubCategory) break;
        }
        if (!parentSubCategory) return;

        // Cascade subcategory: if all menu items in parent subcategory are unchecked, uncheck subcategory
        const allItemsUnchecked = parentSubCategory.menu_items.every(mi => !updatedMenuItems[mi.id]);
        if (allItemsUnchecked) {
            const updatedSubCategories = { ...selectedSubCategories, [parentSubCategory.id]: false };
            setSelectedSubCategories(updatedSubCategories);

            // Cascade category: if all subcategories of parent category are unchecked, uncheck category
            const allSubsUnchecked = parentCategory.SubCategories.every(sub => !updatedSubCategories[sub.id]);
            if (allSubsUnchecked) {
                setSelectedCategories(prev => ({ ...prev, [parentCategory.id]: false }));
            }
        }
    };

    const validationSchema = yup.object().shape({
        name: yup.string().trim().required('Name is required').max(20, 'Name must be 20 characters or less'),
        menu_items_list: yup.array().min(1, 'At least one item must be selected').required('This field is required'),
    });

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            name: data?.name || '',
            menu_items_list: Object.keys(selectedMenuItems).filter(menuItemId => selectedMenuItems[menuItemId])
        },
        validationSchema: validationSchema,
        onSubmit: values => {
            setLoader(true);
            const menuItemsList = Object.keys(selectedMenuItems).filter(menuItemId => selectedMenuItems[menuItemId]);
            let formData = new FormData();
            if (!isCopy) {
                formData.append('id', params.id);
            }
            formData.append('name', values.name);
            formData.append('menu_items_list', JSON.stringify(menuItemsList));
            formData.append('brand_id', getBrandId());
            formData.append('company_id', getCompany().company_id);


            const addedCategories = [];
            const removedCategories = [];
            Object.entries(selectedCategories).forEach(([id, isChecked]) => {
                if (initialSelectedCategories[id]==undefined || isChecked!=initialSelectedCategories[id]) {
                    if(isChecked){
                        addedCategories.push(Number(id));
                    }else{
                        removedCategories.push(Number(id));
                    }
                }
            });

            const addedSubCategories = [];
            const removedSubCategories = [];
            Object.entries(selectedSubCategories).forEach(([id, isChecked]) => {
                if (initialSelectedSubCategories[id] == undefined || isChecked != initialSelectedSubCategories[id]) {
                    if (isChecked) {
                        addedSubCategories.push(Number(id));
                    } else {
                        removedSubCategories.push(Number(id));
                    }
                }
            });

            console.log('addedCategories', addedCategories);
            console.log('removedCategories', removedCategories);
            console.log('addedSubCategories', addedSubCategories);
            console.log('removedSubCategories', removedSubCategories);

            formData.append('added_categories', JSON.stringify(addedCategories));
            formData.append('removed_categories', JSON.stringify(removedCategories));
            formData.append('added_sub_categories', JSON.stringify(addedSubCategories));
            formData.append('removed_sub_categories', JSON.stringify(removedSubCategories));

            // Send only the rows whose fetching_order changed, each trimmed to
            // just category_id / sub_category_id / fetching_order. menu_id is
            // sent once at the top level.
            const changedOrders = [];
            categories.forEach((category) => {
                if (String(category.fetching_order) !== String(initialOrders.categories[category.id])) {
                    changedOrders.push({
                        category_id: category.id,
                        sub_category_id: null,
                        fetching_order: category.fetching_order,
                    });
                }
                (category.SubCategories || []).forEach((sub) => {
                    if (String(sub.fetching_order) !== String(initialOrders.subCategories[sub.id])) {
                        changedOrders.push({
                            category_id: null,
                            sub_category_id: sub.id,
                            fetching_order: sub.fetching_order,
                        });
                    }
                });
            });

            const submitMenuItems = () => {
                EditMenuItems(formData).then(response => {
                    setLoader(false);
                    if (response.data.status === 1 || response.data.status === "1") {
                        Swal.fire({
                            customClass: 'swal-wide',
                            icon: 'success',
                            title: response.data.message,
                            showConfirmButton: false,
                            timer: 1500,
                            toast: true,
                            position: 'top-right',
                        });
                        setTimeout(() => {
                            navigate(`/company/menu/menu/menu_card/list/${searchParams.get('pageno') || 1}`);
                        }, 1000);
                    } else {
                        Swal.fire({
                            customClass: 'swal-wide',
                            icon: 'error',
                            title: response.data.message,
                            showConfirmButton: false,
                            timer: 5000,
                            toast: true,
                            position: 'top-right',
                        });
                    }
                }).catch(error => {
                    setLoader(false);
                    console.error("error ====> ", error);
                });
            };

            if (isCopy) {
                const orderRows = [];
                categories.forEach((category) => {
                    orderRows.push({
                        category_id: category.id,
                        sub_category_id: null,
                        fetching_order: category.fetching_order,
                    });
                    (category.SubCategories || []).forEach((sub) => {
                        orderRows.push({
                            category_id: null,
                            sub_category_id: sub.id,
                            fetching_order: sub.fetching_order,
                        });
                    });
                });

                addMenuItems(formData).then(response => {
                    setLoader(false);
                    if (response.data.status === 1 || response.data.status === "1") {
                        const newMenuId = response.data.response?.id;
                        if (orderRows.length) {
                            updateMenuCategoryOrder({ menu_id: newMenuId, categories: orderRows, operation_type: 'create_menu' })
                                .catch((error) => {
                                    console.log("Error===>", error)
                                });
                        }
                        Swal.fire({
                            customClass: 'swal-wide',
                            icon: 'success',
                            title: response.data.message,
                            showConfirmButton: false,
                            timer: 1500,
                            toast: true,
                            position: 'top-right',
                        });
                        setTimeout(() => {
                            navigate(`/company/menu/menu/menu_card/list/${searchParams.get('pageno') || 1}`);
                        }, 1000);
                    } else {
                        Swal.fire({
                            customClass: 'swal-wide',
                            icon: 'error',
                            title: response.data.message,
                            showConfirmButton: false,
                            timer: 5000,
                            toast: true,
                            position: 'top-right',
                        });
                    }
                }).catch(error => {
                    setLoader(false);
                    console.error("error ====> ", error);
                });
                return;
            }

            // Skip the order call when nothing changed so we don't wipe existing rankings.
            if (changedOrders.length) {
                updateMenuCategoryOrder({ menu_id: params.id, categories: changedOrders, operation_type: 'update_menu' })
                    .then((response) => {
                        if (response.data.status === 1 || response.data.status === "1") {
                            submitMenuItems();
                        }
                    })
                    .catch((error) => {
                        setLoader(false);
                        console.log("Error===>", error)
                    });
            } else {
                submitMenuItems();
            }
        }
    });


    // One box searches all three levels: a category, a sub category or an item,
    // by name (partial) or by id (exact). An empty box matches everything, so the
    // predicates below double as "show all" when nothing is typed.
    const searchTerm = menuSearch.trim().toLowerCase();
    const matchesSearch = (name, id) => !searchTerm
        || String(name ?? '').toLowerCase().includes(searchTerm)
        || String(id ?? '') === searchTerm;
    // A match higher up keeps everything below it, so hitting a category shows its
    // whole tree while hitting an item narrows its sub category to that item.
    const isCategoryMatched = (category) => matchesSearch(category?.name, category?.id);
    const isSubCategoryMatched = (category, subCategory) =>
        isCategoryMatched(category) || matchesSearch(subCategory?.name, subCategory?.id);
    const isMenuItemVisible = (category, subCategory, menuItem) =>
        isSubCategoryMatched(category, subCategory) || matchesSearch(menuItem?.item_name, menuItem?.id);
    const isSubCategoryVisible = (category, subCategory) =>
        isSubCategoryMatched(category, subCategory)
        || (subCategory?.menu_items || []).some(menuItem => matchesSearch(menuItem?.item_name, menuItem?.id));
    const isCategoryVisible = (category) =>
        isCategoryMatched(category)
        || (category?.SubCategories || []).some(subCategory => isSubCategoryVisible(category, subCategory));
    // Counts behind the result chips, and the flag the empty state hangs off.
    const searchResults = categories.reduce((counts, category) => {
        if (!isCategoryVisible(category)) return counts;
        counts.categories += 1;
        (category?.SubCategories || []).forEach(subCategory => {
            if (!isSubCategoryVisible(category, subCategory)) return;
            counts.subCategories += 1;
            counts.menuItems += (subCategory?.menu_items || [])
                .filter(menuItem => isMenuItemVisible(category, subCategory, menuItem)).length;
        });
        return counts;
    }, { categories: 0, subCategories: 0, menuItems: 0 });
    const hasSearchResults = searchResults.categories > 0;

    // Wrap the matched part of a name so the hit stands out in a long tree.
    const highlightMatch = (text) => {
        const label = capitalizeFirstLetter(text);
        if (!searchTerm) return label;
        const index = String(label).toLowerCase().indexOf(searchTerm);
        if (index < 0) return label;
        return <>
            {label.slice(0, index)}
            <mark className='menu-search-hit'>{label.slice(index, index + searchTerm.length)}</mark>
            {label.slice(index + searchTerm.length)}
        </>;
    };

    const handelCategoryOrderChanges = (categoryIndex, value) => {
        let tempCategories = [...categories];
        tempCategories[categoryIndex].fetching_order = value;
        setCategories(tempCategories);
    };

    const handelSubCategoryOrderChanges = (categoryIndex, subCategoryIndx, value) => {
        let tempCategories = [...categories];
        tempCategories[categoryIndex].SubCategories[subCategoryIndx].fetching_order = value;
        setCategories(tempCategories);
    }

    return (
        <>
            {
                loader
                ?
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999,
                        backgroundColor: 'rgba(255,255,255,0.5)'
                    }}>
                        <Loader />
                    </div>
                : null
            }

            <div className="page-header d-flex align-items-center">
                <div className="page-header-left">
                    <Breadcrums data={breadcrumbs} />
                    <h4>{isCopy ? 'Copy Menu' : 'Edit Menu'}</h4>
                </div>
                <div className="page-header-right ms-auto"></div>
            </div>
            <form onSubmit={formik.handleSubmit}>
                <div className="row row-sm">
                    <div className="card custom-card">
                        <div className="card-body">
                            <div className="row row-sm">
                                <div className='col-lg-6 form-group mt-3'>
                                    <label htmlFor='name' className='text-left d-flex'>
                                        Name <span className="requirestar">*</span>
                                    </label>
                                    <input
                                        name='name'
                                        id="name"
                                        type='text'
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.name}
                                        placeholder='Enter name'
                                        className='form-control'
                                    />
                                    <span className='text-danger d-flex text-left'>
                                        {formik.errors.name && formik.touched.name && formik.errors.name}
                                    </span>
                                </div>
                                <label className='mt-4 ' style={{ fontWeight: '600' }}>Select Items</label>
                                <div className='col-lg-12 form-group mt-2 mb-3'>
                                    <div className='menu-search-wrap'>
                                        <i className='ti ti-search menu-search-icon'></i>
                                        <input
                                            type='text'
                                            className='form-control menu-search-input'
                                            value={menuSearch}
                                            onChange={(e) => setMenuSearch(e.target.value)}
                                            placeholder='Search category, sub category or item by name or id'
                                        />
                                        {menuSearch ?
                                            <button type='button' className='menu-search-clear' onClick={() => setMenuSearch('')} aria-label='Clear search'>
                                                <i className='ti ti-close'></i>
                                            </button> : null}
                                    </div>
                                    {searchTerm && hasSearchResults ?
                                        <div className='menu-search-meta'>
                                            <span className='menu-search-chip'>{searchResults.categories} {searchResults.categories === 1 ? 'category' : 'categories'}</span>
                                            <span className='menu-search-chip'>{searchResults.subCategories} {searchResults.subCategories === 1 ? 'sub category' : 'sub categories'}</span>
                                            <span className='menu-search-chip'>{searchResults.menuItems} {searchResults.menuItems === 1 ? 'item' : 'items'}</span>
                                        </div> : null}
                                </div>
                                {searchTerm && !hasSearchResults ?
                                    <div className='menu-search-empty'>
                                        <i className='ti ti-search'></i>
                                        <span>No category, sub category or item matches “{menuSearch.trim()}”</span>
                                    </div> : null}
                                {categories.map((category, index) => (
                                    !isCategoryVisible(category) ? null :
                                    <div key={category.id} className={`category-label${isInactive(category) ? ' menu-category-inactive' : ''}`}>
                                        <div className='d-flex align-items-center'>
                                            <label className='d-flex align-items-center cp mb-0' style={{ userSelect: 'none' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={!!selectedCategories[category.id]}
                                                    onChange={() => handleCategoryChange(category.id)}
                                                />
                                                {category?.image && (
                                                    <img
                                                        src={category.image}
                                                        alt={category?.name || 'category'}
                                                        className='category-thumb mx-2'
                                                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                                    />
                                                )}
                                                <span className='label-span' title={isInactive(category) ? INACTIVE_TITLE : undefined}>{highlightMatch(category.name)}</span>{inactiveSubCategoriesHint(category)}
                                            </label>
                                            <input
                                                type="text"
                                                name='category_order'
                                                placeholder="Enter order"
                                                className="form-control form-control-sm mx-2"
                                                style={{ width: '85px', height: '18px' }}
                                                value={category?.fetching_order}
                                                onChange={(e) => {
                                                    // Allow only digits and prevent non-numeric values
                                                    const value = e.target.value;
                                                    if (/^\d+$/.test(value) || value === '') {
                                                        handelCategoryOrderChanges(index, value);
                                                    }
                                                }}
                                                pattern="\d+"  // Allows only whole numbers
                                                title="Please enter a whole and positive number"
                                            />
                                        </div>
                                        {(selectedCategories[category.id] || searchTerm) && category.SubCategories.length > 0 && (
                                            <div className="subcategory-label mt-2">
                                                {category.SubCategories.map((subCategory, subCategoryIndx) => (
                                                    !isSubCategoryVisible(category, subCategory) ? null :
                                                    <div key={subCategory.id} className={isInactive(subCategory) ? 'menu-subcategory-inactive' : undefined}>
                                                        <div className='d-flex align-items-center mb-2'>
                                                            <label className='d-flex align-items-center cp mb-0' style={{ userSelect: 'none' }}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={!!selectedSubCategories[subCategory.id]}
                                                                    onChange={() => handleSubCategoryChange(subCategory.id)}
                                                                />
                                                                <span className='label-span' title={isInactive(subCategory) ? INACTIVE_TITLE : undefined}>{highlightMatch(subCategory.name)}</span>{inactiveItemsHint(subCategory)}
                                                            </label>
                                                            <input
                                                                type="text"
                                                                name="sub_category_order"
                                                                placeholder="Enter order"
                                                                className="form-control form-control-sm mx-2"
                                                                style={{ width: '90px', height: '18px' }}
                                                                value={subCategory?.fetching_order}
                                                                onChange={(e) => {
                                                                    // Allow only digits and prevent non-numeric values
                                                                    const value = e.target.value;
                                                                    if (/^\d+$/.test(value) || value === '') {
                                                                        handelSubCategoryOrderChanges(index, subCategoryIndx, value);
                                                                    }
                                                                }}
                                                                pattern="\d+"  // Allows only whole numbers
                                                                title="Please enter a whole and positive number"
                                                            />

                                                        </div>
                                                        {(selectedSubCategories[subCategory.id] || searchTerm) && subCategory.menu_items.length > 0 && (
                                                            <div className="menu-item submenu-label">
                                                                {subCategory.menu_items.filter(menuItem => isMenuItemVisible(category, subCategory, menuItem)).map(menuItem => (
                                                                    <div key={menuItem.id} className={isInactive(menuItem) ? 'menu-chip-inactive' : undefined} title={isInactive(menuItem) ? INACTIVE_TITLE : undefined}>
                                                                        <label>
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={!!selectedMenuItems[menuItem.id]}
                                                                                onChange={() => handleMenuItemChange(menuItem.id)}
                                                                            />
                                                                            <span className='label-span'>{highlightMatch(menuItem.item_name)}</span>
                                                                        </label>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                                <span className='text-danger d-flex text-left'>
                                    {formik.errors.menu_items_list && formik.touched.menu_items_list && formik.errors.menu_items_list}
                                </span>
                                <div className="mt-2">
                                    <button className="btn btn-primary" type="submit">
                                        {isCopy ? 'Add' : 'Update'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
            <ToastContainer
                position="top-right"
                hideProgressBar={false}
                autoClose={1000}
                newestOnTop={true}
                closeOnClick={false}
                draggable={false}
                rtl={false}
            />
        </>
    );
}

export default EditMenu